/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-syntax */
import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import Button from 'components/Button';
import Loading from 'components/Loading';
import i18n from 'i18n';
import RestoreIcon from '@material-ui/icons/Restore';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import Permission from 'components/ValidPermission';
import {
  editMaintenanceRequest,
  getMaintenanceRequest,
  clearMaintenanceData,
} from 'modules/clients/actions';
import { ClientsMaintenanceDisplayWarningProps } from 'modules/clients/interfaces';
import { messagesRequest } from 'modules/messages/actions';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import {
  adjustDateByTwoHours,
  formatDateToISO,
  formatDateWithMoment,
  isEmptyOrNull,
  removeHTMLTags,
} from 'utils/helpers';
import {
  ClientMaintenanceFields,
  ClientMaintenanceHistoryResponse,
} from 'modules/maintenance/interfaces';
import {
  getClientMaintenanceRequest,
  getClientMaintenanceHistoryRequest,
  setClientMaintenanceRequest,
} from 'modules/maintenance/actions';
import DynamicTextField from 'components/DynamicTextField';
import TitleDivider from 'components/TitleDivider';
import { Formik, Form } from 'formik';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import moment from 'moment';
import styles, { overrideModal } from './styles';
import ScheduleForm, {
  MaintenanceSchedule,
  scheduleInitialValues,
} from './ScheduleForm';
import ImmediateForm, {
  immediateInitialValues,
  MaintenanceImmediate,
} from './ImmediateForm';
import DisplayForm, {
  displayInitialValues,
  MaintenanceDisplay,
} from './DisplayForm';
import ClientsTemplate from '../Templates/Clients';
import screenFields from './screenFields';
import schema from './schema';
import MaintenanceHistoryClient from './MaintenanceHistoryClient';

const useStyles = makeStyles(styles);

interface ValuesProps {
  display: MaintenanceDisplay;
  immediate: MaintenanceImmediate;
  schedule: MaintenanceSchedule;
}

const initialValues: ValuesProps = {
  display: displayInitialValues,
  immediate: immediateInitialValues,
  schedule: scheduleInitialValues,
};

interface Props {
  clientMaintenance: ClientMaintenanceFields;
  isLoadingClientMaintenance: boolean;
  clientsMaintenanceDisplayWarning: ClientsMaintenanceDisplayWarningProps;
  companySelected: CompanyData | null;
  isLoading: boolean;
  permissions: PermissionsData | undefined;
  role?: RolesEnum;
  clientMaintenanceHistory: ClientMaintenanceHistoryResponse;
  getMaintenanceRequest: ({ id }) => void;
  editMaintenanceRequest: ({ id, displayValues, changedItems }) => void;
  clearMaintenanceData: () => void;
  getClientMaintenanceRequest: ({ id }) => Promise<void>;
  setClientMaintenanceRequest: (props) => Promise<void>;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  getClientMaintenanceHistoryRequest: (props) => void;
}

const MaintenanceClients = ({
  clientMaintenance,
  isLoadingClientMaintenance,
  clientsMaintenanceDisplayWarning,
  companySelected,
  isLoading,
  permissions,
  role,
  clientMaintenanceHistory,
  getMaintenanceRequest,
  editMaintenanceRequest,
  clearMaintenanceData,
  getClientMaintenanceRequest,
  setClientMaintenanceRequest,
  modalOpen,
  modalClose,
  getClientMaintenanceHistoryRequest,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [values, setValues] = React.useState<ValuesProps>(initialValues);
  const validateDisplay = (values: any) => {
    return !!(
      values?.display?.active === true &&
      (adjustDateByTwoHours(values?.display?.start_date) <
        formatDateToISO(new Date()) ||
        adjustDateByTwoHours(values?.display?.end_date) <
          adjustDateByTwoHours(values?.display?.start_date) ||
        !moment(values?.display?.start_date).isValid() ||
        !moment(values?.display?.end_date).isValid())
    );
  };

  const validateSchedule = (values: any) => {
    return !!(
      values?.schedule?.enabled === true &&
      (adjustDateByTwoHours(values?.schedule?.start_datetime) <
        formatDateToISO(new Date()) ||
        adjustDateByTwoHours(values?.schedule?.end_datetime) <
          adjustDateByTwoHours(values?.schedule?.start_datetime) ||
        !moment(values?.schedule?.start_datetime).isValid() ||
        !moment(values?.schedule?.end_datetime).isValid())
    );
  };

  const [hasError, setHasError] = React.useState<boolean>(
    validateDisplay(values)
  );
  const [hasMaintenanceError, setHasMaintenanceError] = React.useState<boolean>(
    validateSchedule(values)
  );

  React.useEffect(() => {
    setHasError(validateDisplay(values));
  }, [values]);

  React.useEffect(() => {
    setHasMaintenanceError(validateSchedule(values));
  }, [values]);

  const handleSetMaintenance = maintenanceValues => {
    const maintenanceScheduleDetails = [
      { labelKey: 'STATUS_KEY', value: i18n.t('ACTIVE_KEY') },
      {
        labelKey: 'START_ALERT_KEY',
        value: formatDateWithMoment(maintenanceValues?.schedule.start_datetime),
      },
      {
        labelKey: 'END_ALERT_KEY',
        value: formatDateWithMoment(maintenanceValues?.schedule.end_datetime),
      },
      {
        labelKey: 'TERMINATE_SESSIONS_KEY',
        value: i18n.t(
          maintenanceValues?.schedule?.terminate_sessions
            ? 'ACTIVE_KEY'
            : 'INACTIVE_KEY'
        ),
      },
      {
        labelKey: 'MESSAGE_KEY',
        value: removeHTMLTags(String(maintenanceValues?.schedule.message)),
      },
    ];
    const maintenanceImmediateDetails = [
      { labelKey: 'STATUS_KEY', value: i18n.t('ACTIVE_KEY') },
      {
        labelKey: 'MESSAGE_KEY',
        value: removeHTMLTags(String(maintenanceValues?.immediate.message)),
      },
    ];
    modalOpen({
      title: i18n.t('MAINTENANCE_CONFIG_KEY'),
      content: (
        <>
          <Grid container>
            <Typography className={classes.maintenanceTitle}>{`${i18n.t(
              'MAINTENANCE_SCHEDULED_KEY'
            )}: `}</Typography>
            <Grid container>
              {maintenanceValues?.schedule?.enabled ? (
                <Grid
                  item
                  xs={12}
                  className={classes.maintenanceDetailsBoxActive}
                >
                  {maintenanceScheduleDetails.map((detail, index) => (
                    <Typography
                      key={index}
                      className={classes.maintenanceDetailsText}
                      component="div"
                    >
                      <Box fontWeight="bold" display="inline">
                        {`${i18n.t(detail?.labelKey)}: `}
                      </Box>
                      {detail?.value}
                    </Typography>
                  ))}
                </Grid>
              ) : (
                <Grid
                  item
                  xs={12}
                  className={classes.maintenanceDetailsBoxInactive}
                >
                  <Typography
                    className={classes.maintenanceDetailsText}
                    component="div"
                  >
                    <Box fontWeight="bold" display="inline">
                      {`${i18n.t('STATUS_KEY')}: `}
                    </Box>
                    {i18n.t('INACTIVE_KEY')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid container>
            <Typography className={classes.maintenanceTitle}>{`${i18n.t(
              'MAINTENANCE_MANUAL_KEY'
            )}: `}</Typography>
            <Grid container>
              {maintenanceValues?.immediate?.enabled ? (
                <Grid
                  item
                  xs={12}
                  className={classes.maintenanceDetailsBoxActive}
                >
                  {maintenanceImmediateDetails.map((detail, index) => (
                    <Typography
                      key={index}
                      className={classes.maintenanceDetailsText}
                      component="div"
                    >
                      <Box fontWeight="bold" display="inline">
                        {`${i18n.t(detail?.labelKey)}: `}
                      </Box>
                      {detail?.value}
                    </Typography>
                  ))}
                </Grid>
              ) : (
                <Grid
                  item
                  xs={12}
                  className={classes.maintenanceDetailsBoxInactive}
                >
                  <Typography
                    className={classes.maintenanceDetailsText}
                    component="div"
                  >
                    <Box fontWeight="bold" display="inline">
                      {`${i18n.t('STATUS_KEY')}: `}
                    </Box>
                    {i18n.t('INACTIVE_KEY')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>

          <TitleDivider overrideClasses={{ title: { marginTop: 8 } }}>
            {`${i18n.t('CONFIRM_MAINTENANCE_CONFIG_KEY')}:`}
          </TitleDivider>
          <Formik
            initialValues={{ reason: '' }}
            enableReinitialize
            validationSchema={schema}
            validateOnChange
            validateOnBlur
            onSubmit={() => modalClose()}
          >
            {({ setFieldValue, errors, values, isValid }) => {
              return (
                <>
                  <Form>
                    <Grid container>
                      <Grid container spacing={2} className={classes.fields}>
                        <DynamicTextField
                          {...screenFields[0]}
                          setFieldValue={setFieldValue}
                          value={values?.reason}
                          errors={errors}
                        />
                      </Grid>

                      <Grid
                        container
                        justify="flex-end"
                        className={classes.deleteButtons}
                      >
                        <Button
                          submitButton
                          id="confirm-button"
                          onClick={() => {
                            setClientMaintenanceRequest({
                              id,
                              values: {
                                current_maintenance:
                                  maintenanceValues?.immediate,
                                future_maintenance: maintenanceValues?.schedule,
                                reason: values?.reason,
                              },
                            });
                            modalClose();
                          }}
                          disabled={!values.reason || !isValid}
                          isLoading={isLoadingClientMaintenance}
                        >
                          {i18n.t('CONFIRM_KEY')}
                        </Button>
                        <Button
                          id="cancel-button"
                          variant="outlined"
                          onClick={modalClose}
                        >
                          {i18n.t('CANCEL_KEY')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                </>
              );
            }}
          </Formik>
        </>
      ),
    });
  };

  const handleSubmit = () => {
    const startValues = {
      display: clientsMaintenanceDisplayWarning,
      schedule: clientMaintenance?.future_maintenance ?? scheduleInitialValues,
      immediate:
        clientMaintenance?.current_maintenance ?? immediateInitialValues,
    };

    const changedItems: string[] = [];
    let displayChanged = false;
    let scheduleChanged = false;
    let immediateChanged = false;

    if (startValues.display !== values.display) {
      displayChanged = true;
    }

    for (const key of ['schedule', 'immediate']) {
      const startValue = startValues[key];
      const value = values[key];

      // eslint-disable-next-line no-continue
      if (!value) continue;

      if (typeof startValue === 'object') {
        const subKeys = Object.keys(startValue);

        for (const subKey of subKeys) {
          if (startValue[subKey] !== value[subKey]) {
            if (key === 'schedule') {
              scheduleChanged = true;
            } else if (key === 'immediate') {
              immediateChanged = true;
            }
            changedItems.push(key);
            break;
          }
        }
      }
    }

    if (displayChanged && !scheduleChanged && !immediateChanged) {
      editMaintenanceRequest({
        id,
        displayValues: values.display,
        changedItems: ['display'],
      });
    }

    if ((scheduleChanged || immediateChanged) && !displayChanged) {
      handleSetMaintenance(values);
    }

    if (displayChanged && (scheduleChanged || immediateChanged)) {
      editMaintenanceRequest({
        id,
        displayValues: values.display,
        changedItems: ['display'],
      });

      handleSetMaintenance(values);
    }
  };

  React.useEffect(() => {
    setValues(state => ({
      ...state,
      display: clientsMaintenanceDisplayWarning,
      schedule: clientMaintenance.future_maintenance,
      immediate: clientMaintenance.current_maintenance,
    }));
  }, [clientsMaintenanceDisplayWarning, clientMaintenance, id]);

  React.useEffect(() => {
    getClientMaintenanceRequest({ id });
  }, [getClientMaintenanceRequest, id]);

  React.useEffect(() => {
    getMaintenanceRequest({ id });
  }, [getMaintenanceRequest, id]);

  React.useEffect(() => {
    return () => clearMaintenanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisplayInvalid =
    values.display.active &&
    [values.display.start_date, values.display.end_date].some(isEmptyOrNull);

  const isScheduleInvalid =
    values?.schedule?.enabled &&
    ['start_datetime', 'end_datetime'].some(
      key =>
        isEmptyOrNull(values?.schedule?.[key]) ||
        !values?.schedule?.hasOwnProperty(key)
    );

  const disabledButton =
    hasError || hasMaintenanceError || isDisplayInvalid || isScheduleInvalid;

  const hasMaintenanceHistoryPermission =
    !!permissions && permissions?.client_maintenance_history;

  React.useEffect(() => {
    if (hasMaintenanceHistoryPermission) {
      getClientMaintenanceHistoryRequest({ id, query: { limit: 500 } });
    }
  }, [getClientMaintenanceHistoryRequest, id]);

  const handleOpenMaintenance = () => {
    modalOpen({
      title: i18n.t('DETAILS_MAINTENANCE_HISTORY_KEY'),
      content: (
        <MaintenanceHistoryClient
          currentRequest={clientMaintenanceHistory?.results[0]?.request_uuid}
          currentRequestDate={String(
            clientMaintenanceHistory?.results[0]?.updated_at
          )}
        />
      ),
      overrideClasses: overrideModal,
      maxWidth: 'md',
      footer: (
        <Grid>
          <Button id="close-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  return (
    <ClientsTemplate
      current="maintenance"
      title={i18n.t('ENVIRONMENTS_PAGE.MAINTENANCE_TITLE_KEY')}
      breadcrumbs={[
        {
          name: i18n.t('CLIENT_KEY'),
          link: '/clients',
        },
        {
          name: i18n.t('MAINTENANCE_KEY'),
        },
      ]}
      button={
        <>
          <Permission value="client_maintenance_history">
            <Button
              onClick={handleOpenMaintenance}
              className={classes.maintenanceHistoryButton}
              endIcon={<RestoreIcon />}
              disabled={isLoading || isLoadingClientMaintenance}
            >
              {i18n.t('MAINTENANCE_HISTORY_KEY')}
            </Button>
          </Permission>
        </>
      }
    >
      {!isLoadingClientMaintenance && !isLoading ? (
        <>
          <Grid container className={classes.cardsContent}>
            <Permission value="list_client_scheduled_warnings">
              <Grid item className={classes.maintenanceManual} xs={12} md={6}>
                <DisplayForm
                  disabled={!permissions?.edit_client_scheduled_warnings}
                  role={role}
                  defaultValue={values?.display}
                  onChange={(display: MaintenanceDisplay) => {
                    setValues({ ...values, display });
                  }}
                  setHasError={setHasError}
                />
              </Grid>
            </Permission>
            <Permission value="get_client_maintenance">
              <Grid item className={classes.maintenanceManual} xs={12} md={6}>
                <ScheduleForm
                  disabled={!permissions?.edit_client_maintenance}
                  role={role}
                  companySelected={companySelected}
                  defaultValue={values?.schedule}
                  setHasError={setHasMaintenanceError}
                  onChange={(schedule: MaintenanceSchedule) => {
                    setValues({
                      ...values,
                      schedule,
                      immediate: {
                        ...values?.immediate,
                        enabled: false,
                      },
                    });
                  }}
                />
              </Grid>
            </Permission>
            <Permission value="get_client_maintenance">
              <Grid item className={classes.maintenanceManual} xs={12} md={6}>
                <ImmediateForm
                  disabled={!permissions?.edit_client_maintenance}
                  role={role}
                  companySelected={companySelected}
                  defaultValue={values?.immediate}
                  onChange={(immediate: MaintenanceImmediate) => {
                    setValues({
                      ...values,
                      immediate,
                      schedule: {
                        ...values?.schedule,
                        enabled: false,
                      },
                    });
                  }}
                />
              </Grid>
            </Permission>
          </Grid>

          <Grid container justify="flex-end" className={classes.buttons}>
            <Button
              disabled={disabledButton}
              submitButton
              id="save-button"
              isLoading={isLoadingClientMaintenance || isLoading}
              onClick={handleSubmit}
            >
              {i18n.t('SAVE_KEY')}
            </Button>
            <Button
              id="cancel-button"
              variant="outlined"
              onClick={() => history.push('/clients')}
            >
              {i18n.t('CANCEL_KEY')}
            </Button>
          </Grid>
        </>
      ) : (
        <Loading />
      )}
    </ClientsTemplate>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
  clients: { isLoading, clientsMaintenanceDisplayWarning },
  maintenance: {
    isLoadingClientMaintenance,
    clientMaintenance,
    clientMaintenanceHistory,
  },
}: ApplicationState) => ({
  isLoading,
  clientsMaintenanceDisplayWarning,
  clientMaintenance,
  isLoadingClientMaintenance,
  permissions: data?.permissions,
  companySelected,
  role: data?.role,
  clientMaintenanceHistory,
});

export default connect(mapStateToProps, {
  getMaintenanceRequest,
  editMaintenanceRequest,
  messagesRequest,
  clearMaintenanceData,
  getClientMaintenanceRequest,
  setClientMaintenanceRequest,
  modalOpen,
  modalClose,
  getClientMaintenanceHistoryRequest,
})(MaintenanceClients);
